import { useCallback, useState } from "react"
import { useClient } from "urql"

import type {
  GetPriceChangeNotificationPdfPreviewFailure,
  GetPriceChangePreviewPdfQuery,
  GetPriceChangePreviewPdfQueryVariables,
} from "@/graphql/codegen/graphql"
import { GetPriceChangePreviewPdfDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface PriceChangePreviewRecipient {
  companyId?: string | null
  contactId?: string | null
}

interface UseGetPreviewPriceChangePDFReturn {
  isPreviewFetching: boolean
  handlePreview: (emailNote?: string | null, recipient?: PriceChangePreviewRecipient) => Promise<void>
}

export const useGetPriceChangePreview = (submissionId: string): UseGetPreviewPriceChangePDFReturn => {
  const client = useClient()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [isFetching, setIsFetching] = useState(false)

  const handlePreview = useCallback(
    async (emailNote?: string | null, recipient?: PriceChangePreviewRecipient): Promise<void> => {
      if (!submissionId || !emailNote) {
        return
      }

      setIsFetching(true)

      try {
        const { data, error } = await client
          .query<GetPriceChangePreviewPdfQuery, GetPriceChangePreviewPdfQueryVariables>(
            GetPriceChangePreviewPdfDocument,
            {
              input: {
                submissionId,
                emailNote,
                companyId: recipient?.companyId ?? undefined,
                contactId: recipient?.contactId ?? undefined,
              },
            },
            { requestPolicy: "network-only" }
          )
          .toPromise()

        const output = data?.priceChange?.getPriceChangeNotificationPdfPreview

        const hasError = error || output?.__typename === "GetPriceChangeNotificationPdfPreviewFailure"

        if (hasError) {
          const message =
            error?.message ??
            (output as GetPriceChangeNotificationPdfPreviewFailure | undefined)?.error.message ??
            "Failed to generate the preview pdf"

          notify({
            type: NotificationType.Error,
            message,
          })

          return
        }

        const previewUrl = output?.signedUrl

        if (!previewUrl) {
          return
        }

        window.open(previewUrl, "_blank", "noopener,noreferrer")
      } finally {
        setIsFetching(false)
      }
    },
    [client, notify, submissionId]
  )

  return {
    isPreviewFetching: isFetching,
    handlePreview,
  }
}
